// Live preview of the mobile welcome screen — a phone frame rendered from the
// card's draft, so the owner sees the result while typing.
//
// Deliberately NOT built on layout/PhoneMockup: that component takes no
// children, is aria-hidden, and hardcodes the auth aside's home-screen artwork.
// This one shows real user data, so it is announced rather than hidden, and the
// frame is page-scoped.
//
// The legal line is styled spans, not anchors: it is a picture of the mobile
// screen, and a real link here would invite a click that goes nowhere.

import { Trans, useTranslation } from 'react-i18next';
import { Icon } from '@/components/common';

interface WelcomeScreenPreviewProps {
  imageUrl?: string;
  /** Shown inside the empty circle when a file is picked but not yet uploaded. */
  imageName?: string;
  title: string;
  description: string;
  /** Empty string means the link is switched off and must not be named. */
  termsLink: string;
  privacyLink: string;
}

const WelcomeScreenPreview = ({
  imageUrl,
  imageName,
  title,
  description,
  termsLink,
  privacyLink,
}: WelcomeScreenPreviewProps) => {
  const { t } = useTranslation();

  const terms = t('company_settings_legal_terms_short');
  const privacy = t('company_settings_legal_privacy_short');
  const hasTerms = termsLink.trim() !== '';
  const hasPrivacy = privacyLink.trim() !== '';

  return (
    <div className='company-settings__preview'>
      <p className='company-settings__preview-label'>{t('company_settings_preview')}</p>

      <div
        className='company-settings__phone'
        role='img'
        aria-label={t('company_settings_preview')}
      >
        <span className='company-settings__phone-notch' aria-hidden='true' />
        <div className='company-settings__phone-screen'>
          <span className='company-settings__phone-image'>
            {imageUrl ? (
              <img src={imageUrl} alt='' className='company-settings__phone-img' />
            ) : (
              <>
                <Icon name='image' size={26} />
                {imageName && (
                  <span className='company-settings__phone-image-name'>{imageName}</span>
                )}
              </>
            )}
          </span>

          <p className='company-settings__phone-title'>{title}</p>
          <p className='company-settings__phone-text'>{description}</p>

          <span className='company-settings__phone-cta'>{t('company_settings_get_started')}</span>

          {/* Only names the links that are actually switched on; with neither,
              the sentence would be meaningless, so it is not rendered. */}
          {(hasTerms || hasPrivacy) && (
            <p className='company-settings__phone-legal'>
              {hasTerms && hasPrivacy ? (
                <Trans
                  i18nKey='company_settings_preview_legal_both'
                  values={{ first: terms, second: privacy }}
                  components={{
                    a: <span className='company-settings__phone-legal-link' />,
                    b: <span className='company-settings__phone-legal-link' />,
                  }}
                />
              ) : (
                <Trans
                  i18nKey='company_settings_preview_legal_one'
                  values={{ first: hasTerms ? terms : privacy }}
                  components={{ a: <span className='company-settings__phone-legal-link' /> }}
                />
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreenPreview;
